'use client';

import { motion } from 'motion/react';
import Stat from '@/components/sections/stats/stat';
import SectionHeader from '@/components/shared/section-header';
import { SanityInlineText } from '@/components/shared/sanity-text';
import { SectionCtaPair, sectionCtaPairFromContent } from '@/components/shared/section-cta-pair';
import { useSectionMotionVariants } from '@/components/shared/motion';
import { useScrollRevealMotion } from '@/hooks/use-scroll-reveal-motion';
import type { StatsSectionContent } from '@/sanity/lib/sections';

export default function Stats({ content }: { content?: StatsSectionContent | null }) {
  const { sectionGridRevealVariants, sectionSequenceBlockVariants, sectionSequenceRevealVariants } =
    useSectionMotionVariants();
  const scrollReveal = useScrollRevealMotion();

  if (!content || content.show === false) return null;

  const stats =
    content.showStats === false
      ? []
      : (content.stats ?? []).flatMap((stat) =>
          typeof stat.value === 'number' && stat.label
            ? [
                {
                  value: stat.value,
                  suffix: stat.suffix ?? '',
                  label: stat.label,
                },
              ]
            : [],
        );

  const { primary, secondary } = sectionCtaPairFromContent(content.showCTA, content.ctaButtons);
  const { header } = content;
  const hasStats = stats.length > 0;
  const hasCtas = Boolean(primary);
  const hasHeaderBody = Boolean(header?.tagline || header?.heading?.length || header?.subheading);
  const showHeaderBlock = content.showHeader !== false && hasHeaderBody;

  if (!hasStats && !showHeaderBlock && !hasCtas) return null;

  return (
    <section
      id="stats"
      className="relative bg-neutral-900 dark:bg-black dark:border-y py-6 sm:py-12"
    >
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 min-[1324px]:px-0">
        <motion.div
          className="space-y-10"
          {...scrollReveal}
          variants={sectionSequenceRevealVariants}
        >
          {showHeaderBlock ? (
            <SectionHeader
              sequenceChild
              className="text-left sm:text-center"
              content={{
                eyebrow: header?.tagline,
                description: header?.subheading,
              }}
              titleSlot={
                header?.heading?.length ? (
                  <h2 className="text-3xl sm:text-5xl leading-tight font-light tracking-tight text-white">
                    <SanityInlineText
                      blocks={header.heading}
                      emphasisClassName="italic font-normal text-[#FCFAD7] dark:text-[#FCFAD7]"
                    />
                  </h2>
                ) : undefined
              }
              eyebrowClassName="text-[#FCFAD7]/80 dark:text-[#FCFAD7]/80"
              emphasisClassName="text-[#FCFAD7] dark:text-[#FCFAD7]"
              headingClassName="text-white"
              subheadingClassName="text-muted-foreground"
            />
          ) : null}
          {hasCtas ? (
            <motion.div variants={sectionSequenceBlockVariants}>
              <SectionCtaPair
                primary={primary}
                secondary={secondary}
                trackingLocation="stats"
                className={showHeaderBlock ? 'justify-center sm:justify-center' : undefined}
              />
            </motion.div>
          ) : null}
          {hasStats ? (
            <motion.div
              variants={sectionGridRevealVariants}
              className="relative mx-auto grid max-w-5xl grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-6"
            >
              {stats.map((s, index: number) => (
                <Stat key={index} s={s} index={index} />
              ))}
            </motion.div>
          ) : null}
        </motion.div>
      </div>
    </section>
  );
}
